import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Typography, 
  Space, 
  Avatar, 
  Upload, 
  Row, 
  Col, 
  Divider,
  Descriptions
} from 'antd';
import { UserOutlined, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { updateUser } from '../store/slices/authSlice';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axios';
import type { UploadProps } from 'antd';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  bio?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (user) {
      console.log('User data in Profile component:', user);
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || '',
        bio: user.bio || '',
      });
    }
  }, [user, form]);

  const handleSubmit = async (values: ProfileForm) => {
    try {
      setLoading(true);
      const response = await api.put('/users/profile', values);
      message.success('Profile updated successfully');
      
      // Update user in Redux store
      if ((response.data as any)?.user) {
        dispatch(updateUser((response.data as any).user));
        await refreshUser(); // Also refresh AuthContext
      }
      
      form.setFieldsValue({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    
    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append('image', file as File); // <-- changed from 'profileImage' to 'image'
      
      const response = await api.post('/users/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if ((response.data as any)?.user) {
        dispatch(updateUser((response.data as any).user));
        await refreshUser(); // Also refresh AuthContext
        message.success('Profile image updated successfully');
        onSuccess?.((response.data as any), file as File);
      }
    } catch (error: any) {
      console.error('Profile image upload error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload profile image';
      message.error(errorMessage);
      onError?.(error as Error);
    } finally {
      setUploadLoading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center mb-8 space-y-4 md:space-y-0">
          <div className="relative">
            <Avatar 
              size={120} 
              src={user?.profileImage ? `${import.meta.env.VITE_API_URL || ''}${user.profileImage}` : undefined} 
              icon={<UserOutlined />}
              className="border-4 border-gray-200"
            />
            <Upload
              customRequest={handleImageUpload}
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button
                type="primary"
                shape="circle"
                icon={<CameraOutlined />}
                loading={uploadLoading}
                className="absolute -bottom-2 -right-2"
                size="small"
              />
            </Upload>
          </div>
          
          <div className="md:ml-6 flex-1">
            <Title level={2} className="mb-2">
              {user?.firstName} {user?.lastName}
            </Title>
            <Text type="secondary" className="text-lg">
              {user?.email}
            </Text>
            
            {/* Basic Info Cards */}
            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} sm={12} md={8}>
                <Card size="small" className="text-center">
                  <Text strong>Role</Text>
                  <div className="text-lg capitalize">{user?.role?.toLowerCase() || 'N/A'}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card size="small" className="text-center">
                  <Text strong>Member Since</Text>
                  <div className="text-lg">{formatDate(user?.created_at)}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card size="small" className="text-center">
                  <Text strong>Last Updated</Text>
                  <div className="text-lg">{formatDate(user?.updated_at)}</div>
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        <Divider />

        {/* Profile Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            phone: user?.phone || '',
            bio: user?.bio || '',
          }}
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter your first name' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter your last name' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input size="large" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
              >
                <Input size="large" placeholder="Enter your phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="bio"
            label="Bio"
            help="Tell us a little about yourself"
          >
            <TextArea 
              rows={4} 
              placeholder="Write a brief bio about yourself..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Divider />

          <Title level={4} className="mt-6 mb-4">Change Password</Title>

          <Row gutter={[24, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[
                  {
                    required: form.getFieldValue('newPassword') ? true : false,
                    message: 'Please enter your current password',
                  },
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  {
                    required: form.getFieldValue('currentPassword') ? true : false,
                    message: 'Please enter your new password',
                  },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  {
                    required: form.getFieldValue('newPassword') ? true : false,
                    message: 'Please confirm your new password',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                Save Changes
              </Button>
              <Button onClick={() => form.resetFields()} size="large">
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Profile; 